import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCompletedTimers } from "../../context/CompletedTimersContext";
import { useAuth } from "../AuthContext";
import {
  FaHome,
  FaCalendarAlt,
  FaCalendarPlus,
  FaCalendarCheck,
  FaChartBar,
  FaUsers,
  FaBriefcase,
  FaAngleDown,
  FaAngleRight,
  FaCog,
  FaTag,
  FaUserMd,
  FaTh,
  FaClock,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaTags,
  FaIdCard,
  FaCashRegister,
  FaUserClock,
  FaPaperPlane,
} from "react-icons/fa";
import "./StaffSidebar.css";

// SIDEBAR-POLISH-001 (Sprint 8): grouped + collapsible IA + aggressive role-gate.
// Spec: docs/design/SIDEBAR-POLISH-001-design.md
// Folded sub-scope: "My Schedule" nav-link per SCHED-AVAIL-DASHBOARD-001-design.md §5.

// Section identifiers — used for auto-expand keyed lookup + state.
const SECTION_SESSIONS = "sessions";
const SECTION_CUSTOMERS = "customers";
const SECTION_WORKFORCE = "workforce";
const SECTION_ADMIN = "admin";

// Path-prefix lists per section. A current route that startsWith ANY of a
// section's prefixes causes that section to auto-expand on initial mount.
const SECTION_ROUTE_PREFIXES = {
  [SECTION_SESSIONS]: [
    "/suite-assignments",
    "/staff/all-suite-calendars",
    "/staff/suite-calendar",
    "/staff/active-sessions",
  ],
  [SECTION_CUSTOMERS]: [
    "/staff/customers",
    "/staff/corporate-programs",
    // FEEDBACK-SOFT-HIDE-001: /staff/feedback prefix removed; route unmounted.
    "/staff/promo-codes",
  ],
  [SECTION_WORKFORCE]: [
    "/staff/massage-availability",
    "/staff/clock",
    "/staff/my-availability",
    "/staff/my-schedule",
    "/staff/timelogs",
    "/staff/payroll",
    "/staff/schedule",
    "/staff/availability",
    "/staff/staff-profiles",
    "/staff/job-codes",
    "/staff/communications",
  ],
  [SECTION_ADMIN]: [
    "/staff/analytics",
    "/staff/sales-report",
    "/pos",
    "/staff/studio-settings",
  ],
};

// Pick the FIRST section whose route-prefixes match the current path, else
// default to WORKFORCE (per spec §3.4 step 4).
const pickInitialSection = (pathname) => {
  const order = [
    SECTION_SESSIONS,
    SECTION_CUSTOMERS,
    SECTION_WORKFORCE,
    SECTION_ADMIN,
  ];
  for (const section of order) {
    const prefixes = SECTION_ROUTE_PREFIXES[section];
    if (prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return section;
    }
  }
  return SECTION_WORKFORCE;
};

const StaffSidebar = () => {
  const { completedTimers } = useCompletedTimers();
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  // Sales report: admins, or anyone given the "sales_report" permission
  // (the permission is in the login token; the server checks it again).
  const canSeeSalesReport =
    isAdmin ||
    (Array.isArray(user?.permissions) && user.permissions.includes("sales_report"));

  // Section open-state. Keyed map: section-id -> boolean.
  // Initial seed: the section matching the current route is open; all others closed.
  const initialOpenSection = useMemo(
    () => pickInitialSection(location.pathname),
    // Seed only on first mount — manual toggles + nav-into-other-section
    // are handled in the useEffect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [openSections, setOpenSections] = useState({
    [SECTION_SESSIONS]: initialOpenSection === SECTION_SESSIONS,
    [SECTION_CUSTOMERS]: initialOpenSection === SECTION_CUSTOMERS,
    [SECTION_WORKFORCE]: initialOpenSection === SECTION_WORKFORCE,
    [SECTION_ADMIN]: initialOpenSection === SECTION_ADMIN,
  });

  // Nested Suite Calendars chevron (pre-existing pattern, preserved).
  const [showSuiteCalendars, setShowSuiteCalendars] = useState(false);

  // When the route changes to a path inside a section, auto-EXPAND that
  // section so the active nav-item is visible (Scenario 5 + 6). Manual
  // expansions on other sections persist across navigation in the same
  // session — we ADD to the open set, never collapse what the user opened.
  // (Story AC Scenario 6: "Customers is still expanded ... And the section
  // containing the new route also expands automatically".)
  useEffect(() => {
    const next = pickInitialSection(location.pathname);
    setOpenSections((prev) => (prev[next] ? prev : { ...prev, [next]: true }));
  }, [location.pathname]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSectionKeyDown = (e, section) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleSection(section);
    }
  };

  const suites = [
    { suiteType: "sauna", suiteNumber: 1 },
    { suiteType: "sauna", suiteNumber: 2 },
    { suiteType: "sauna", suiteNumber: 3 },
    { suiteType: "sauna", suiteNumber: 4 },
    { suiteType: "sauna", suiteNumber: 5 },
    { suiteType: "sauna", suiteNumber: 6 },
    { suiteType: "sauna", suiteNumber: 7 },
    { suiteType: "sauna", suiteNumber: 8 },
    { suiteType: "redlight", suiteNumber: 9 },
    { suiteType: "redlight", suiteNumber: 10 },
  ];

  // Helper function to check if a link is active
  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  // Reusable section-header renderer. Mirrors the existing Suite Calendars
  // chevron pattern (pre-polish StaffSidebar.js:75-122) verbatim in shape.
  const renderSectionHeader = (section, label) => {
    const isOpen = openSections[section];
    return (
      <div
        className={`sidebar-section-header ${isOpen ? "open" : ""}`}
        onClick={() => toggleSection(section)}
        onKeyDown={(e) => handleSectionKeyDown(e, section)}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={`section-${section}`}
      >
        <span className="sidebar-section-label">{label}</span>
        {isOpen ? (
          <FaAngleDown className="dropdown-icon" />
        ) : (
          <FaAngleRight className="dropdown-icon" />
        )}
      </div>
    );
  };

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <h2>Staff Portal</h2>
      </div>
      <ul className="sidebar-menu">
        {/* Top-level Dashboard — always visible, outside all sections. */}
        <li className="sidebar-item">
          <Link
            to="/staff"
            className={`sidebar-link ${
              isActive("/staff") && !isActive("/staff/") ? "active" : ""
            }`}
          >
            <FaHome className="sidebar-icon" />
            <span>Dashboard</span>
          </Link>
        </li>

        {/* ============ SESSIONS — visible to all roles ============ */}
        <li className="sidebar-section">
          {renderSectionHeader(SECTION_SESSIONS, "Sessions")}
          {openSections[SECTION_SESSIONS] && (
            <ul
              className="sidebar-section-body"
              id={`section-${SECTION_SESSIONS}`}
              role="region"
              aria-label="Sessions navigation"
            >
              <li className="sidebar-item">
                <Link
                  to="/suite-assignments"
                  className={`sidebar-link ${
                    isActive("/suite-assignments") ? "active" : ""
                  }`}
                >
                  <FaTh className="sidebar-icon" />
                  <span>Suite Assignments</span>
                </Link>
              </li>
              <li className="sidebar-item">
                <div
                  className={`sidebar-link dropdown-toggle ${
                    isActive("/staff/suite-calendar") ||
                    isActive("/staff/all-suite-calendars")
                      ? "active"
                      : ""
                  }`}
                  onClick={() => setShowSuiteCalendars(!showSuiteCalendars)}
                >
                  <FaCalendarAlt className="sidebar-icon" />
                  <span>Suite Calendars</span>
                  {showSuiteCalendars ? (
                    <FaAngleDown className="dropdown-icon" />
                  ) : (
                    <FaAngleRight className="dropdown-icon" />
                  )}
                </div>
                {showSuiteCalendars && (
                  <ul className="sidebar-submenu">
                    <li className="submenu-item">
                      <Link
                        to="/staff/all-suite-calendars"
                        className={`submenu-link ${
                          isActive("/staff/all-suite-calendars")
                            ? "active"
                            : ""
                        }`}
                      >
                        View 1-Day Calendars
                      </Link>
                    </li>
                    {suites.map((suite) => (
                      <li
                        key={`${suite.suiteType}-${suite.suiteNumber}`}
                        className="submenu-item"
                      >
                        <Link
                          to={`/staff/suite-calendar/${suite.suiteType}/${suite.suiteNumber}`}
                          className="submenu-link"
                        >
                          {suite.suiteType === "sauna"
                            ? `Sauna #${suite.suiteNumber}`
                            : `Red Light #${suite.suiteNumber}`}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              <li className="sidebar-item">
                <Link
                  to="/staff/active-sessions"
                  className={`sidebar-link ${
                    isActive("/staff/active-sessions") ? "active" : ""
                  }`}
                >
                  <FaCalendarAlt className="sidebar-icon" />
                  <span>Active Sessions</span>
                  {completedTimers.length > 0 && (
                    <span className="badge">{completedTimers.length}</span>
                  )}
                </Link>
              </li>
            </ul>
          )}
        </li>

        {/* ============ CUSTOMERS — admin only ============ */}
        {isAdmin && (
          <li className="sidebar-section">
            {renderSectionHeader(SECTION_CUSTOMERS, "Customers")}
            {openSections[SECTION_CUSTOMERS] && (
              <ul
                className="sidebar-section-body"
                id={`section-${SECTION_CUSTOMERS}`}
                role="region"
                aria-label="Customers navigation"
              >
                <li className="sidebar-item">
                  <Link
                    to="/staff/customers"
                    className={`sidebar-link ${
                      isActive("/staff/customers") ? "active" : ""
                    }`}
                  >
                    <FaUsers className="sidebar-icon" />
                    <span>Customer Management</span>
                  </Link>
                </li>
                <li className="sidebar-item">
                  <Link
                    to="/staff/corporate-programs"
                    className={`sidebar-link ${
                      isActive("/staff/corporate-programs") ? "active" : ""
                    }`}
                  >
                    <FaBriefcase className="sidebar-icon" />
                    <span>Corporate Programs</span>
                  </Link>
                </li>
                {/* FEEDBACK-SOFT-HIDE-001: "Customer Feedback" admin-only
                    nav-item removed. /staff/feedback route is unmounted in
                    App.js. Promo Codes admin-only entry retained per the
                    SIDEBAR-POLISH-001 role-gate posture. */}
                <li className="sidebar-item">
                  <Link
                    to="/staff/promo-codes"
                    className={`sidebar-link ${
                      isActive("/staff/promo-codes") ? "active" : ""
                    }`}
                  >
                    <FaTag className="sidebar-icon" />
                    <span>Promo Codes</span>
                  </Link>
                </li>
                {/* PROMO-RF-E — Stripe-sourced staff promo management
                    (list + detail; RF-F adds create/archive). DISTINCT
                    from the legacy in-house "Promo Codes" entry above,
                    which ADR-029 D4 retires via PROMO-RF-H. Additive
                    nav-item; same admin/staff gate as the section. */}
                <li className="sidebar-item">
                  <Link
                    to="/staff/promo-management"
                    className={`sidebar-link ${
                      isActive("/staff/promo-management") ? "active" : ""
                    }`}
                  >
                    <FaTags className="sidebar-icon" />
                    <span>Promo Management</span>
                  </Link>
                </li>
              </ul>
            )}
          </li>
        )}

        {/* ============ WORKFORCE — header for all; items role-gated ============ */}
        <li className="sidebar-section">
          {renderSectionHeader(SECTION_WORKFORCE, "Workforce")}
          {openSections[SECTION_WORKFORCE] && (
            <ul
              className="sidebar-section-body"
              id={`section-${SECTION_WORKFORCE}`}
              role="region"
              aria-label="Workforce navigation"
            >
              <li className="sidebar-item">
                {/* SIDEBAR-MASSAGE-AVAILABILITY-IA — moved from SESSIONS to
                    WORKFORCE; logically belongs alongside My Availability /
                    My Schedule / Daily Review / Timesheets. Top-of-section
                    placement reflects cross-team-visibility priority. */}
                <Link
                  to="/staff/massage-availability"
                  className={`sidebar-link ${
                    isActive("/staff/massage-availability") ? "active" : ""
                  }`}
                >
                  <FaUserMd className="sidebar-icon" />
                  <span>Massage Availability</span>
                </Link>
              </li>
              <li className="sidebar-item">
                <Link
                  to="/staff/clock"
                  className={`sidebar-link ${
                    isActive("/staff/clock") ? "active" : ""
                  }`}
                >
                  <FaClock className="sidebar-icon" />
                  <span>Clock In / Out</span>
                </Link>
              </li>
              <li className="sidebar-item">
                {/* SCHED-SLICE1-H — staff self-service availability page. */}
                <Link
                  to="/staff/my-availability"
                  className={`sidebar-link ${
                    isActive("/staff/my-availability") ? "active" : ""
                  }`}
                >
                  <FaCalendarCheck className="sidebar-icon" />
                  <span>My Availability</span>
                </Link>
              </li>
              <li className="sidebar-item">
                {/* SCHED-AVAIL-DASHBOARD-001 §5 — folded sub-scope.
                    Route shipped by SCHED-SLICE1-G PR #129. Visible to all roles. */}
                <Link
                  to="/staff/my-schedule"
                  className={`sidebar-link ${
                    isActive("/staff/my-schedule") ? "active" : ""
                  }`}
                >
                  <FaCalendarCheck className="sidebar-icon" />
                  <span>My Schedule</span>
                </Link>
              </li>
              <li className="sidebar-item">
                <Link
                  to="/staff/timelogs/today"
                  className={`sidebar-link ${
                    isActive("/staff/timelogs/today") ? "active" : ""
                  }`}
                >
                  <FaClipboardList className="sidebar-icon" />
                  <span>Daily Review</span>
                </Link>
              </li>
              <li className="sidebar-item">
                <Link
                  to="/staff/payroll/timesheets"
                  className={`sidebar-link ${
                    isActive("/staff/payroll/timesheets") ? "active" : ""
                  }`}
                >
                  <FaFileInvoiceDollar className="sidebar-icon" />
                  <span>Timesheets</span>
                </Link>
              </li>
              <li className="sidebar-item">
                {/* MAILCHIMP-COMMS-001-B — Send Communication compose surface.
                    Route mounted in App.js with allowedRoles=[admin, staff,
                    massage_therapist]; BE re-checks via
                    authorizeBroadcastSender. Visible to all sidebar viewers
                    per Roderick Sprint 9 lock; defense-in-depth role gates
                    live at the BE. */}
                <Link
                  to="/staff/communications/broadcast"
                  className={`sidebar-link ${
                    isActive("/staff/communications/broadcast") ? "active" : ""
                  }`}
                >
                  <FaPaperPlane className="sidebar-icon" />
                  <span>Send Communication</span>
                </Link>
              </li>
              <li className="sidebar-item">
                {/* MAILCHIMP-COMMS-001-C — Broadcast History audit view.
                    Same RBAC as compose; BE staff-scopes the list query
                    to senderUserId=self. Pure-additive nav entry — keeps
                    the Workforce section IA intact. */}
                <Link
                  to="/staff/communications/history"
                  className={`sidebar-link ${
                    isActive("/staff/communications/history") ? "active" : ""
                  }`}
                >
                  <FaPaperPlane className="sidebar-icon" />
                  <span>Broadcast History</span>
                </Link>
              </li>
              {isAdmin && (
                <li className="sidebar-item">
                  <Link
                    to="/staff/schedule"
                    className={`sidebar-link ${
                      isActive("/staff/schedule") &&
                      !isActive("/staff/schedule/audit")
                        ? "active"
                        : ""
                    }`}
                  >
                    <FaCalendarPlus className="sidebar-icon" />
                    <span>Schedule</span>
                  </Link>
                </li>
              )}
              {isAdmin && (
                <li className="sidebar-item">
                  {/* SCHED-AVAIL-DASHBOARD-001-B — admin All Availability matrix.
                      Route mounted in App.js with allowedRoles={["admin"]}; the
                      sidebar gate here is defense-in-depth visual hygiene. */}
                  <Link
                    to="/staff/availability"
                    className={`sidebar-link ${
                      isActive("/staff/availability") ? "active" : ""
                    }`}
                  >
                    <FaUserClock className="sidebar-icon" />
                    <span>Staff Availability</span>
                  </Link>
                </li>
              )}
              {isAdmin && (
                <li className="sidebar-item">
                  <Link
                    to="/staff/staff-profiles"
                    className={`sidebar-link ${
                      isActive("/staff/staff-profiles") ? "active" : ""
                    }`}
                  >
                    <FaIdCard className="sidebar-icon" />
                    <span>Staff Profiles</span>
                  </Link>
                </li>
              )}
              {isAdmin && (
                <li className="sidebar-item">
                  <Link
                    to="/staff/job-codes"
                    className={`sidebar-link ${
                      isActive("/staff/job-codes") ? "active" : ""
                    }`}
                  >
                    <FaTags className="sidebar-icon" />
                    <span>Job Codes</span>
                  </Link>
                </li>
              )}
            </ul>
          )}
        </li>

        {/* ============ ADMIN — admin only (Sales Report also for the
            "sales_report" permission) ============ */}
        {canSeeSalesReport && (
          <li className="sidebar-section">
            {renderSectionHeader(SECTION_ADMIN, "Admin")}
            {openSections[SECTION_ADMIN] && (
              <ul
                className="sidebar-section-body"
                id={`section-${SECTION_ADMIN}`}
                role="region"
                aria-label="Admin navigation"
              >
                {isAdmin && (
                  <li className="sidebar-item">
                    <Link
                      to="/staff/analytics"
                      className={`sidebar-link ${
                        isActive("/staff/analytics") ? "active" : ""
                      }`}
                    >
                      <FaChartBar className="sidebar-icon" />
                      <span>Analytics</span>
                    </Link>
                  </li>
                )}
                <li className="sidebar-item">
                  <Link
                    to="/staff/sales-report"
                    className={`sidebar-link ${
                      isActive("/staff/sales-report") ? "active" : ""
                    }`}
                  >
                    <FaFileInvoiceDollar className="sidebar-icon" />
                    <span>Sales Report</span>
                  </Link>
                </li>
                {isAdmin && (
                  <li className="sidebar-item">
                    <Link
                      to="/pos"
                      className={`sidebar-link ${
                        isActive("/pos") ? "active" : ""
                      }`}
                    >
                      <FaCashRegister className="sidebar-icon" />
                      <span>Point of Sale</span>
                    </Link>
                  </li>
                )}
                {isAdmin && (
                  <li className="sidebar-item">
                    <Link
                      to="/staff/studio-settings"
                      className={`sidebar-link ${
                        isActive("/staff/studio-settings") ? "active" : ""
                      }`}
                    >
                      <FaCog className="sidebar-icon" />
                      <span>Studio Settings</span>
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}
      </ul>
    </nav>
  );
};

export default StaffSidebar;
